/* eslint-disable max-lines */
import React, { useState, useCallback, useEffect, useContext, useRef, createRef } from 'react';
import _ from 'lodash';
import axios from 'axios';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import type { Moment } from 'moment';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { Link, useHistory } from 'react-router-dom';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Typography from '@material-ui/core/Typography';
import Drawer from '@material-ui/core/Drawer';
import Dialog from '@material-ui/core/Dialog';
import Paper from '@material-ui/core/Paper';
import Container from '@material-ui/core/Container';
import ListItemText from '@material-ui/core/ListItemText';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import FilterNoneIcon from '@material-ui/icons/FilterNone';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CreateIcon from '@material-ui/icons/Create';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import CloseIcon from '@material-ui/icons/Close';
import ShareIcon from '@material-ui/icons/Share';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import EventNoteIcon from '@material-ui/icons/EventNote';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import type { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import InputLabel from '@material-ui/core/InputLabel';
import NativeSelect from '@material-ui/core/NativeSelect';
import muiTheme from '@this/components/appoint/mui_theme';
// import ScheduleIcon from '@material-ui/icons/Schedule';
import type { CalendarArgs } from '@this/domain/appoint/calendar';
import Calendar from '@this/domain/appoint/calendar';
import type {
  AdjustmentResponseArgs,
  AdjustmentMeetingType,
  AdjustmentStatus
} from '@this/domain/appoint/adjustment';
import Adjustment, { convertAdjustmentResponseToArgs } from '@this/domain/appoint/adjustment';
import type { UserSettingResponse } from '@this/domain/appoint/user_setting';
import UserSetting, { convertUserSettingResponseToArgs } from '@this/domain/appoint/user_setting';
import Candidate from '@this/domain/appoint/candidate';
import type { MeetingTitleHistoryArgs } from '@this/domain/appoint/meeting_title_history';
import MeetingTitleHistory from '@this/domain/appoint/meeting_title_history';
import type { EventResponse } from '@this/domain/appoint/event';
import Event, { convertEventResponseToArgs } from '@this/domain/appoint/event';
import type { AdjustmentFinishedType, AdjustmentsByStatusType } from '@this/components/appoint/common';
import { Right, ButtonWrapper, ButtonProgress, CloseButton } from '@this/components/appoint/common';
import AppointContext from '@this/components/appoint/appoint_context';
import Utils from '@this/domain/appoint/utils';
import DrawerMenu from '../drawer_menu';
import { AuthBackdrop, TheReAuthButton, AutocompleteBox } from '../adjustments/new/new';
import PlaceInput from '../adjustments/new/place_input';
import ParticipantInput from '../adjustments/new/participant_input';
import DashboardCandidateArea from './candidate_area';
import BigCalendar from './dashboard_big_calendar';
import DashboardCoachMark from './dashboard_coach_mark';
import LeftMenu from './left_menu/left_menu';

export interface Holiday {
  date: string;
  name: string;
}

interface Props {
  menuButtonRef: React.MutableRefObject<null>;
  openHeadDrawer: boolean;
  setOpenHeadDrawer: (value: boolean) => void;
}

interface Response {
  need_auth: boolean;
  primary_calendar_id: string;
  calendars: CalendarArgs[];
  user_setting: UserSettingResponse;
  disconnected_accounts: { email: string; message: string | null }[];
  adjustment: AdjustmentResponseArgs;
  meeting_title_histories: MeetingTitleHistoryArgs[];
}

interface Params {
  calendar_checked: string;
}

interface AdjustmentsResponse {
  adjustments: AdjustmentResponseArgs[];
}

interface EventsResponse {
  events: EventResponse[][];
  holidays: Holiday[];
}

interface SubmitParams {
  start: string;
  end: string;
  guest_name?: string;
  participants?: { id: string; auth_data_id: string }[];
}

type EventLists = { [calendarId: string]: Event[] };

const Dashboard = observer(({ menuButtonRef, openHeadDrawer, setOpenHeadDrawer }: Props) => {
  const contextValue = useContext(AppointContext);
  const history = useHistory();
  const { calendars, setCalendars } = contextValue;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAdjustmentLoading, setIsAdjustmentLoading] = useState<boolean>(false);
  const [isEventLoading, setIsEventLoading] = useState<boolean>(false);
  const [isAdjustmentEditing, setIsAdjustmentEditing] = useState<boolean>(false);
  const [isAdjustmentUpdating, setIsAdjustmentUpdating] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false); // 調整一覧
  const [openOpenMeetingDrawer, setOpenOpenMeetingDrawer] = useState<boolean>(false); // 公開URL一覧
  const [openLeftColumn, setOpenLeftColumn] = useState<boolean>(true);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [showCandidateDialog, setShowCandidateDialog] = useState<boolean>(false);
  const [showShareDialog, setShowShareDialog] = useState<boolean>(false);
  const [showNameSettingDialog, setShowNameSettingDialog] = useState<boolean>(false);
  const [showNameSettingFirstFlag, setShowNameSettingFirstFlag] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>('');
  const [needAuth, setNeedAuth] = useState<boolean>(false);
  const [needReAuth, setNeedReAuth] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [adjustmentUpdaterrorMessage, setAdjustmentUpdateErrorMessage] = useState<string | null>(null);
  const [selectedCalendars, setSelectedCalendars] = useState<Calendar[]>([]);
  const [meetingTitleHistories, setMeetingTitleHistories] = useState<MeetingTitleHistory[]>([]);
  const [disconnectedAccounts, setDisconnectedAccounts] = useState<{ email: string; message: string | null }[]>(
    []
  );
  const [eventLists, setEventLists] = useState<EventLists>({});
  const [adjustments, setAdjustments] = useState<AdjustmentsByStatusType>({
    adjusting: [],
    fixed: [],
    cancelled: []
  });
  const [adjustmentsFinished, setAdjustmentsFinished] = useState<AdjustmentFinishedType>({
    adjusting: false,
    fixed: false,
    cancelled: false
  });
  const [selectedAdjustment, setSelectedAdjustment] = useState<Adjustment | null>(null);
  const [editingAdjustment, setEditingAdjustment] = useState<Adjustment | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [selectedDate, setSelectedDate] = useState<MaterialUiPickersDate>(null);
  const [date, setDate] = useState<Moment>(moment());
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [currentStatusTab, setCurrentStatusTab] = useState<AdjustmentStatus>('adjusting');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [cancelErrorMessage, setCancelErrorMessage] = useState<string | null>(null);
  const [userSetting, setUserSetting] = useState<UserSetting | null>(null);
  const [showZoomAuthDialog, setShowZoomAuthDialog] = useState<boolean>(false);

  const adjustmentsLoader = useRef<HTMLLIElement | null>(null);
  const cancelDialogPaper = useRef<HTMLLIElement | null>(null);
  const candidateDialogPaper = useRef<HTMLLIElement | null>(null);

  const newAdjustmentButtonRef = useRef(null);

  const firstCalendarUpdate = useRef(true);

  const fetchEventsForWeekPromise = async (
    calendars: Calendar[],
    currentDate: Moment,
    week: 'this' | 'last' | 'next'
  ) => {
    // カレンダーが月曜始まりなので、「今週の月曜日」を取得したい。
    // 2021/06/06(日)時点では、2021/05/31(月)をstartMomentにセットしたい。
    // 2021/06/07(月)時点では、2021/06/07(月)をstartMomentにセットしたい。
    // day(1)は、曜日番号を1に変更するだけなので、日曜日(0)から見て前週の月曜日を取得するためには、
    // 1日戻る必要があるためsubtract(1, 'days')を設定している。
    const thisMonday = currentDate.clone().subtract(1, 'days').day(1);
    const diff = { this: 0, last: -1, next: 1 };
    const startMoment = thisMonday.add(diff[week], 'weeks');
    const endMoment = startMoment.clone().add(6, 'days');

    const params = {
      calendars: calendars.map(c => ({ id: c.id, name: c.name, email: c.email, auth_data_id: c.authDataId })),
      from: startMoment.format('YYYY-MM-DD'),
      to: endMoment.format('YYYY-MM-DD')
    };
    return axios.get<EventsResponse>('/aicalendar/events.json', { params });
  };

  const uniqEventLists = (lists: EventLists): EventLists => {
    const existingIds: (string | undefined)[] = [];
    const result: EventLists = {};
    Object.entries(lists).forEach(([calendarId, events]) => {
      const uniqEvents = _.uniqBy(events, 'id');
      const filteredEvents = _.filter(uniqEvents, e => existingIds.indexOf(e.id) === -1);
      if (filteredEvents.length > 0) {
        result[calendarId] = filteredEvents;
      }
      existingIds.push(..._.map(events, e => e.id));
    });
    return result;
  };

  const joinEventLists = (originalEventLists: EventLists, addEventLists: EventLists): EventLists => {
    const newEventLists = originalEventLists;
    Object.entries(addEventLists).forEach(([calendarId, events]) => {
      if (newEventLists[calendarId]) {
        newEventLists[calendarId].push(...events);
      } else {
        newEventLists[calendarId] = events;
      }
    });
    return newEventLists;
  };

  const fetchEvents = async (selectedCalendars: Calendar[]) => {
    // 今週のeventを取得
    setIsEventLoading(true);
    const eventsResponse = await fetchEventsForWeekPromise(selectedCalendars, date, 'this');
    const thisWeekEventLists: EventLists = {};
    eventsResponse.data.events.forEach(responseForCalendar => {
      const events = responseForCalendar.map(e => new Event(convertEventResponseToArgs(e)));
      if (events.length > 0) {
        const calendarId = _.first(events)!.calendarId!;
        events.forEach(e =>
          e.participants?.forEach(p => {
            const cal = calendars.filter(c => c.id === p.id);
            if (cal.length > 0) {
              p.name = cal[0].name;
            }
          })
        );
        thisWeekEventLists[calendarId] = events;
      }
    });
    setEventLists(uniqEventLists(thisWeekEventLists));
    setHolidays(holidays.concat(eventsResponse.data.holidays).filter((v, i, a) => a.indexOf(v) === i));
    setIsEventLoading(false);

    // 先週・来週のeventを取得
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    Promise.all([
      fetchEventsForWeekPromise(selectedCalendars, date, 'last'),
      fetchEventsForWeekPromise(selectedCalendars, date, 'next')
    ]).then(eventsResponseList => {
      const lastWeekEventsResponse = eventsResponseList[0];
      const nextWeekEventsResponse = eventsResponseList[1];

      const lastWeekEventLists: EventLists = {};
      lastWeekEventsResponse.data.events.forEach(responseForCalendar => {
        const events = responseForCalendar.map(e => new Event(convertEventResponseToArgs(e)));
        if (events.length > 0) {
          const calendarId = _.first(events)!.calendarId!;
          lastWeekEventLists[calendarId] = events;
        }
      });
      const nextWeekEventLists: EventLists = {};
      nextWeekEventsResponse.data.events.forEach(responseForCalendar => {
        const events = responseForCalendar.map(e => new Event(convertEventResponseToArgs(e)));
        if (events.length > 0) {
          const calendarId = _.first(events)!.calendarId!;
          nextWeekEventLists[calendarId] = events;
        }
      });
      const addEventLists = joinEventLists(lastWeekEventLists, nextWeekEventLists);

      const newEventLists = joinEventLists(thisWeekEventLists, addEventLists);
      setEventLists(uniqEventLists(newEventLists));
    });
  };

  const fetchAdjustments = async (status: AdjustmentStatus) => {
    if (adjustmentsFinished[status]) {
      return;
    }

    setIsAdjustmentLoading(true);
    const params = {
      status,
      length: adjustments[status].length
    };
    const res = await axios.get<AdjustmentsResponse>('/aicalendar/adjustments.json', { params });
    const data = res.data;
    if (data.adjustments.length < 10) {
      const tmpFinished = adjustmentsFinished;
      tmpFinished[status] = true;
      setAdjustmentsFinished(tmpFinished);
    }

    adjustments[status] = adjustments[status]
      .concat(data.adjustments.map(a => new Adjustment(convertAdjustmentResponseToArgs(a))))
      .filter((a, i, self) => _.map(self, s => s.id).indexOf(a.id) === i);
    setIsAdjustmentLoading(false);
  };

  // APIから取得した情報をadjustmentに格納する
  const mergeAdjustmentEvent = (selAdjustment: Adjustment, key?: string) => {
    Object.entries(eventLists).forEach(([_calendarId, events]) => {
      const evts = events.filter(e => e.adjustmentKey === key);
      if (evts.length > 0) {
        selAdjustment.description = evts[0].description || selAdjustment.description;
        selAdjustment.title = evts[0].name || selAdjustment.title;
        selAdjustment.place = evts[0].location || selAdjustment.place;
      }
    });
    return selAdjustment;
  };

  const handleDetailOpen = async (key?: string) => {
    setIsAdjustmentEditing(false);
    setEditingAdjustment(null);
    setAdjustmentUpdateErrorMessage(null);
    if (key && key.length === 32) {
      let selected = _.filter(_.flatten(Object.values(adjustments)), a => a.key === key)[0] || null;
      if (!selected) {
        // 取得済みのリストにない場合は該当の調整が出るまで何度も探す処理がadjustments#indexにある(keyが渡された場合)
        const params = { key };
        const res = await axios.get<AdjustmentsResponse>('/aicalendar/adjustments.json', { params });
        const data = res.data;
        const convertedAdms = data.adjustments.map(a => new Adjustment(convertAdjustmentResponseToArgs(a)));
        selected = _.filter(convertedAdms, a => a.key === key)[0];
        const groupedAdms = _.groupBy(convertedAdms, a => a.adjusting);
        const tmpAdms = adjustments;
        tmpAdms.adjusting = tmpAdms.adjusting.concat(
          _.filter(groupedAdms.true, ga => _.map(tmpAdms.adjusting, ta => ta.key).indexOf(ga.key) === -1)
        );
        tmpAdms.fixed = tmpAdms.fixed.concat(
          _.filter(groupedAdms.false, ga => _.map(tmpAdms.fixed, ta => ta.key).indexOf(ga.key) === -1)
        );
        setAdjustments(tmpAdms);
      }
      selected = mergeAdjustmentEvent(selected, key);

      setOpenDrawer(true);
      if (selected.status) {
        setCurrentStatusTab(selected.status);
      }
      setSelectedAdjustment(selected);
      setSelectedDate(selected.candidates[0].start);
      setOpenLeftColumn(true);
    }
  };

  const onlineMeetingUrlRef = createRef<HTMLDivElement>();
  const copyOnlineMeetingUrlToClipBoard = () => {
    Utils.copyElementToClipBoard(onlineMeetingUrlRef.current, false);
    contextValue.setSnackbarContent({
      message: 'Web会議リンクをクリップボードにコピーしました',
      severity: 'success'
    });
    contextValue.setOpenSnackbar(true);
  };

  const fetchData = async (key = '') => {
    setIsLoading(true);
    const locationSearch = new URLSearchParams(location.search);
    const params = {
      key: location.hash === '#created' ? locationSearch.get('adjustment_key') : key.length > 0 ? key : null
    };

    const res = await axios.get<Response>('/aicalendar.json', { params });
    const data = res.data;
    if (data.need_auth) {
      setNeedAuth(data.need_auth);
    } else {
      setNeedReAuth(data.disconnected_accounts.length > 0);
    }
    let calendars = data.calendars.map(c => new Calendar(c));
    calendars = _.sortBy(calendars, c => !c.editable);
    setCalendars(calendars);
    if (data.user_setting === null) {
      setSelectedCalendars([calendars.find(c => c.id === data.primary_calendar_id) || calendars[0]]);
    } else {
      setUserSetting(new UserSetting(convertUserSettingResponseToArgs(data.user_setting)));
      const getCalendars: string[] = JSON.parse(
        data.user_setting.calendar_checked ? data.user_setting.calendar_checked : '[]'
      );
      const calendarChecked: Calendar[] = [];
      getCalendars.forEach(d => {
        calendarChecked.push(calendars.find(c => c.id === d) || calendars[0]);
      });
      if (calendarChecked.length > 0) {
        setSelectedCalendars(calendarChecked);
      } else {
        setSelectedCalendars([calendars.find(c => c.id === data.primary_calendar_id) || calendars[0]]);
      }
    }
    setMeetingTitleHistories(data.meeting_title_histories.map(raw => new MeetingTitleHistory(raw)));
    setDisconnectedAccounts(data.disconnected_accounts);
    if (data.adjustment) {
      setSelectedAdjustment(new Adjustment(convertAdjustmentResponseToArgs(data.adjustment)));
      if (key.length === 0) {
        setShowShareDialog(true);
      } else {
        const adjst = new Adjustment(convertAdjustmentResponseToArgs(data.adjustment));
        const tmpAdms = adjustments;
        tmpAdms.fixed = tmpAdms.fixed.map(a => {
          if (a.key === adjst.key) {
            return adjst;
          }
          return a;
        });
        setAdjustments(tmpAdms);
      }
    }
    setIsLoading(false);
    fetchAdjustments('adjusting');
  };

  const handleObserver = (entities: any[]) => {
    if (entities[0].isIntersecting) {
      fetchAdjustments(currentStatusTab);
    }
  };

  // マウント時に1回発動
  useEffect(() => {
    fetchData();

    const lHash = location.hash;
    const openDrawer = lHash === '#drawerOpened';
    const openOMeetingDrawer = lHash === '#openMeetingDrawer' || lHash === '#openMeetingCreated';
    setOpenDrawer(openDrawer);
    setOpenOpenMeetingDrawer(openOMeetingDrawer);
    setOpenLeftColumn(!openDrawer && !openOMeetingDrawer);

    if (openDrawer || openOMeetingDrawer) {
      location.hash = '';
      setOpenHeadDrawer(false);

      if (lHash === '#openMeetingCreated') {
        contextValue.setSnackbarContent({
          message: '公開URLを作成しました。',
          severity: 'success'
        });
        contextValue.setOpenSnackbar(true);
      }
    }

    Utils.trackEvent('[cal][view] dashboard');
  }, []);

  useEffect(() => {
    // 初期値設定の際の発火はスキップする
    // https://stackoverflow.com/questions/53253940/make-react-useeffect-hook-not-run-on-initial-render
    if (firstCalendarUpdate.current) {
      firstCalendarUpdate.current = false;
      return;
    }
    fetchEvents(selectedCalendars);
  }, [selectedCalendars]);

  const options = {
    root: null,
    rootMargin: '0px',
    threshold: 0.9
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, options);
    if (adjustmentsLoader.current) {
      observer.observe(adjustmentsLoader.current);
    }

    return () => {
      if (adjustmentsLoader.current) {
        observer.unobserve(adjustmentsLoader.current);
      }
    };
  }, [adjustmentsLoader, currentStatusTab]);

  const handleDateChange = (newDate: Moment) => {
    const weekType = newDate.isAfter(date) ? 'next' : 'last';
    setDate(newDate);
    setIsEventLoading(true);
    fetchEventsForWeekPromise(selectedCalendars, newDate, weekType)
      .then(eventsResponse => {
        const weekEventLists: EventLists = {};
        eventsResponse.data.events.forEach(responseForCalendar => {
          const events = responseForCalendar.map(e => new Event(convertEventResponseToArgs(e)));
          if (events.length > 0) {
            weekEventLists[_.first(events)!.calendarId!] = events;
          }
        });
        const newEventLists = joinEventLists(eventLists, weekEventLists);
        setEventLists(uniqEventLists(newEventLists));
        setHolidays(holidays.concat(eventsResponse.data.holidays).filter((v, i, a) => a.indexOf(v) === i));
        setIsEventLoading(false);
      })
      .catch(_e => setIsLoading(false));
  };

  const handleSubmit = async () => {
    if (!selectedAdjustment || !selectedCandidate) {
      return;
    }
    Utils.trackEvent('[cal][click] fix adjustment');
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const params: SubmitParams = {
        start: selectedCandidate.start.format(),
        end: selectedCandidate.end.format(),
        participants: selectedAdjustment.participants.map(p => ({ id: p.id, auth_data_id: p.authDataId }))
      };
      await axios.put(`/aicalendar/adjustments/${selectedAdjustment.key}.json`, params);
      if (selectedAdjustment?.status) {
        adjustments.adjusting = adjustments.adjusting.filter(a => a.id !== selectedAdjustment.id);
        selectedAdjustment.status = 'fixed';
        adjustments.fixed.push(selectedAdjustment);
      }
      setCurrentStatusTab('fixed');
      fetchData(selectedAdjustment.key);
    } catch (e) {
      setErrorMessage('通信エラーが発生しました。時間をおいて再度お試しください。');
    } finally {
      setSelectedAdjustment(null);
      setShowCandidateDialog(false);
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    Utils.trackEvent('[cal][click] cancel adjustment');
    setIsSubmitting(true);
    setCancelErrorMessage(null);
    try {
      if (selectedAdjustment?.status) {
        await axios.delete(`/aicalendar/adjustments/${selectedAdjustment.key}.json`);
        adjustments[selectedAdjustment.status] = adjustments[selectedAdjustment.status].filter(
          a => a.id !== selectedAdjustment.id
        );
        selectedAdjustment.status = 'cancelled';
        adjustments.cancelled.push(selectedAdjustment);
      }
      fetchEvents(selectedCalendars);
    } catch (e) {
      setCancelErrorMessage('通信エラーが発生しました。時間をおいて再度お試しください。');
    } finally {
      setSelectedAdjustment(null);
      setShowCancelDialog(false);
      setIsSubmitting(false);
    }
  };

  const handleAdjustmentUpdate = async () => {
    const editedAdjustment = _.clone(editingAdjustment);
    if (!editedAdjustment || !editedAdjustment.status) {
      return;
    }
    setIsAdjustmentUpdating(true);
    try {
      const params = editedAdjustment.updateParams();
      const res = await axios.put(`/aicalendar/adjustments/${editedAdjustment.key}.json`, params);
      if (res?.data && res.data.error) {
        setAdjustmentUpdateErrorMessage(res.data.error);
      } else {
        setSelectedAdjustment(editedAdjustment);
        const filteredAdjs = Utils.dig(adjustments, editedAdjustment.status);
        const updatedIndex = filteredAdjs.findIndex((a: Adjustment) => a.id === editedAdjustment.id);
        filteredAdjs[updatedIndex] = editedAdjustment;

        fetchEvents(selectedCalendars);
        setIsAdjustmentEditing(false);
        setEditingAdjustment(null);
      }
    } catch (e) {
      setAdjustmentUpdateErrorMessage('通信エラーが発生しました。\n時間をおいて再度お試しください。');
    } finally {
      setIsAdjustmentUpdating(false);
    }
  };

  const toggleIsAdjustmentEditing = () => {
    if (!selectedAdjustment) {
      return;
    }

    if (!isAdjustmentEditing && (!editingAdjustment || editingAdjustment.id !== selectedAdjustment.id)) {
      setEditingAdjustment(_.clone(selectedAdjustment));
    }
    setIsAdjustmentEditing(!isAdjustmentEditing);
  };

  const isTitleEmpty = () => {
    return editingAdjustment?.title.length === 0;
  };

  const handleAddParticipant = (participant: Calendar) => {
    if (!editingAdjustment) {
      return;
    }

    editingAdjustment.participants.push(participant);
    fetchEvents(selectedCalendars);
  };

  const handleRemoveParticipant = (participant: Calendar) => {
    if (!editingAdjustment) {
      return;
    }

    editingAdjustment.removeParticipant(participant);
    fetchEvents(selectedCalendars);
  };

  const handleAdjustmentTimeUpdate = (type: 'start' | 'end', time: any) => {
    if (!editingAdjustment || typeof time !== 'string') {
      return;
    }

    const timeArray = time.split(':');
    if (type === 'start') {
      const newTime = moment(editingAdjustment.startTime?.format());
      editingAdjustment.startTime = newTime.set({ hour: +timeArray[0], minute: +timeArray[1] });
    } else {
      const newTime = moment(editingAdjustment.endTime?.format());
      editingAdjustment.endTime = newTime.set({ hour: +timeArray[0], minute: +timeArray[1] });
    }
  };

  useEffect(() => {
    if (
      !editingAdjustment ||
      !editingAdjustment.startTime ||
      !editingAdjustment.endTime ||
      editingAdjustment.startTime < editingAdjustment.endTime
    ) {
      return;
    }

    const startTime = moment(editingAdjustment.startTime.format());
    editingAdjustment.endTime = startTime.add(editingAdjustment.duration, 'minutes');
  }, [editingAdjustment?.startTime]);

  useEffect(() => {
    if (
      !editingAdjustment ||
      !editingAdjustment.startTime ||
      !editingAdjustment.endTime ||
      editingAdjustment.startTime < editingAdjustment.endTime
    ) {
      return;
    }

    const endTime = moment(editingAdjustment.endTime.format());
    editingAdjustment.startTime = endTime.subtract(editingAdjustment.duration, 'minutes');
  }, [editingAdjustment?.endTime]);

  const leftContainerRef = createRef<HTMLDivElement>();
  const rightContainerRef = createRef<HTMLDivElement>();

  const setUserData = useCallback(async calendars => {
    const params: Params = { calendar_checked: calendars };
    await axios.post('/aicalendar.json', params);
  }, []);

  const handleSelectedCalendarsChange = (calendar: Calendar, checked: boolean) => {
    let sCalendars = selectedCalendars;
    if (checked) {
      sCalendars.push(calendar);
    } else {
      sCalendars = _.filter(sCalendars, sc => sc.id !== calendar.id);
    }

    setSelectedCalendars(_.uniq(sCalendars));
    setUserData(sCalendars.map(c => c.id));
  };

  const handleCurrentStatusTabChange = (status: AdjustmentStatus) => {
    setCurrentStatusTab(status);
  };

  const handleOpenCandidateDialog = () => {
    if (!selectedAdjustment || !selectedAdjustment.adjusting) {
      return;
    }

    setShowCandidateDialog(true);
  };

  const renderCancelDialog = () => (
    <Dialog
      open={showCancelDialog}
      onClose={() => setShowCancelDialog(false)}
      fullWidth
      PaperProps={contextValue.isMobile ? { style: { margin: 16, width: '100%' } } : {}}
    >
      <Paper ref={cancelDialogPaper}>
        {contextValue.isMobile && (
          <Box display="flex" style={{ marginBottom: '-20px', padding: '10px 10px 0 0' }}>
            <CloseButton onClick={() => setShowCancelDialog(false)} />
          </Box>
        )}
        <DialogHead>
          <DialogHeadIcon>
            <EmojiObjectsIcon fontSize="large" />
          </DialogHeadIcon>
          <DialogHeadTitle>予定を削除する</DialogHeadTitle>
          <DialogHeadText>下記のアイテムを削除します。</DialogHeadText>
        </DialogHead>
        <DialogContent isMobile={contextValue.isMobile}>
          <DialogAdjustmentWrap>
            <TheListItemText
              disableTypography
              primary={
                <>
                  <DialogAdjustmentTitle>{selectedAdjustment?.title}</DialogAdjustmentTitle>
                  <DialogAdjustmentDate>{selectedAdjustment?.dateText()}</DialogAdjustmentDate>
                </>
              }
            />
          </DialogAdjustmentWrap>
        </DialogContent>
        <DialogFoot isMobile={contextValue.isMobile}>
          <DialogFootText>削除してもよろしいですか？</DialogFootText>
          <ButtonWrapper>
            <Button
              style={{
                backgroundColor: muiTheme.palette.error.main,
                color: 'white',
                fontSize: '12px'
              }}
              onClick={() => handleCancel()}
            >
              削除
            </Button>
            {isSubmitting && <ButtonProgress />}
          </ButtonWrapper>
        </DialogFoot>
        {cancelErrorMessage && <Error color="error">{cancelErrorMessage}</Error>}
      </Paper>
    </Dialog>
  );

  const toggleSelectedCandidate = (candidate: Candidate) => {
    setSelectedCandidate(
      selectedCandidate && selectedCandidate.start.format() === candidate.start.format() ? null : candidate
    );
  };

  //
  // 以下、stateから計算しているだけのもの。どっかでstore化した方がよさそう
  //
  const slotsForCandidate = (candidate: Candidate) => {
    const slots: Candidate[] = [];
    if (selectedAdjustment) {
      const stepMinutes = 30;
      let slotStart = candidate.start.clone();
      while (true) {
        const slotEnd = slotStart.clone().add(selectedAdjustment.duration, 'minutes');
        if (slotEnd > candidate.end) {
          break;
        }
        slots.push(new Candidate({ start: slotStart, end: slotEnd }));
        slotStart = slotStart.clone().add(stepMinutes, 'minutes');
      }
    }
    return slots;
  };

  let candidateSlots: Candidate[] = [];
  if (selectedAdjustment) {
    let selectedCandidates = selectedAdjustment.candidates.filter(c => {
      return selectedDate && c.start.format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD');
    });
    if (selectedCandidates.length === 0) {
      selectedCandidates = selectedAdjustment.candidates;
    }
    candidateSlots = selectedCandidates
      .map(candidate => {
        return slotsForCandidate(candidate);
      })
      .flat();
  }

  const linkRef = createRef<HTMLDivElement>();
  const copyLinkToClipBoard = () => {
    Utils.copyElementToClipBoard(linkRef.current, false);
    contextValue.setSnackbarContent({
      message: '調整用リンクをクリップボードにコピーしました',
      severity: 'success'
    });
    contextValue.setOpenSnackbar(true);
  };

  const candidatesHTMLRef = createRef<HTMLDivElement>();
  const candidatesTextRef = createRef<HTMLDivElement>();
  const copyCandidatesHTMLToClipBoard = () => {
    Utils.copyHTMLAndTextToClipBoard(
      userSetting?.shareUseHTML ? candidatesHTMLRef.current : candidatesTextRef.current,
      candidatesTextRef.current,
      false
    );
    if (userSetting) {
      const params = userSetting.submitParams();
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      axios.put('/aicalendar/user_settings', params);
    }
    contextValue.setSnackbarContent({
      message: '候補日のHTMLをクリップボードにコピーしました',
      severity: 'success'
    });
    contextValue.setOpenSnackbar(true);
  };

  const handleCloseShareDialog = () => {
    setShowShareDialog(false);
    if (!openDrawer) {
      setSelectedAdjustment(null);
      location.hash = '';
    }
  };

  const renderCandidatesArea = () => (
    <DashboardCandidateArea
      adjustment={selectedAdjustment}
      candidateSlots={candidateSlots}
      selectedCandidate={selectedCandidate}
      setSelectedCandidate={setSelectedCandidate}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      toggleSelectedCandidate={toggleSelectedCandidate}
    />
  );

  const renderCandidateDialog = () => (
    <Dialog
      open={showCandidateDialog}
      maxWidth="md"
      ref={candidateDialogPaper}
      onClose={() => setShowCandidateDialog(false)}
      PaperProps={contextValue.isMobile ? { style: { margin: 16, width: '100%' } } : {}}
    >
      <Paper>
        {contextValue.isMobile && (
          <Box display="flex" style={{ marginBottom: '-20px', padding: '10px 10px 0 0' }}>
            <CloseButton onClick={() => setShowCandidateDialog(false)} />
          </Box>
        )}
        <CardHead isMobile={contextValue.isMobile}>
          <Box display="flex">
            <CardHeadTitle>日程を調整</CardHeadTitle>
            <HelpOutlineIcon style={{ margin: '0 10px 0 5px', color: '#ddd' }} />
          </Box>
          <CardHeadText isMobile={contextValue.isMobile}>
            日時を選んで確定してください。
            <br style={{ display: contextValue.isMobile ? 'block' : 'none' }} />
            確定した日時のみカレンダーに残ります。
          </CardHeadText>
        </CardHead>
        {renderCandidatesArea()}
        <CardFoot display="flex" style={{ marginTop: '40px' }}>
          {errorMessage && <Error>{errorMessage}</Error>}
          <ButtonWrapper style={{ marginLeft: '24px' }}>
            <SubmitButton
              size="large"
              color="primary"
              style={{ color: 'white' }}
              disabled={!selectedCandidate || isSubmitting}
              onClick={() => handleSubmit()}
            >
              日程を確定
            </SubmitButton>
            {isSubmitting && <ButtonProgress />}
          </ButtonWrapper>
        </CardFoot>
      </Paper>
    </Dialog>
  );

  const renderShareDialog = () => (
    <Dialog
      open={showShareDialog}
      maxWidth="md"
      onClose={() => handleCloseShareDialog()}
      PaperProps={contextValue.isMobile ? { style: { margin: 16, width: '100%' } } : {}}
    >
      <Paper style={{ padding: '12px' }}>
        <ShareHead>
          <CloseButton onClick={() => handleCloseShareDialog()} />
          <ShareHeadIcon>
            <ThumbUpIcon fontSize="large" />
          </ShareHeadIcon>
          <ShareTitle>
            {location.hash === '#created' ? (
              <>
                日程調整アイテムを
                <br style={{ display: contextValue.isMobile ? 'block' : 'none' }} />
                新規作成しました
              </>
            ) : (
              '日程調整アイテムをシェアする'
            )}
          </ShareTitle>
          <ShareText>
            調整用URLかメールやチャット用の
            <br style={{ display: contextValue.isMobile ? 'block' : 'none' }} />
            テキストをコピーして送れます
          </ShareText>
        </ShareHead>
        <CardBody isMobile={contextValue.isMobile}>
          <ShareBlock isMobile={contextValue.isMobile}>
            <ShareBlockHead>
              <ShareBlockTitle>調整用URLで送る</ShareBlockTitle>
              <PreviewButton
                size="small"
                color="primary"
                onClick={() =>
                  window.open(
                    history.createHref({
                      pathname: `/adjustments/${selectedAdjustment?.key}`
                    })
                  )
                }
                endIcon={<OpenInNewIcon />}
              >
                プレビュー
              </PreviewButton>
            </ShareBlockHead>
            <ShareBlockBox isMobile={contextValue.isMobile}>
              <CopyTextBox ref={linkRef}>
                <CopyText style={{ overflow: 'hidden' }}>
                  {`${location.origin}${history.createHref({
                    pathname: `/adjustments/${selectedAdjustment?.key}`
                  })}`}
                </CopyText>
              </CopyTextBox>
              <CopyIconWrap>
                <CopyIconButton edge="end" color="primary" aria-label="copy" onClick={() => copyLinkToClipBoard()}>
                  <FilterNoneIcon fontSize="small" style={{ color: '#fff' }} />
                </CopyIconButton>
              </CopyIconWrap>
            </ShareBlockBox>
          </ShareBlock>
          <ShareBlock isMobile={contextValue.isMobile}>
            <ShareBlockHead>
              <ShareBlockTitle>テキストで送る</ShareBlockTitle>
              {userSetting && (
                <ButtonGroup color="primary" size="small" aria-label="button group" style={{ marginLeft: 'auto' }}>
                  <Button
                    variant={userSetting?.shareUseHTML ? 'contained' : 'outlined'}
                    onClick={() => {
                      userSetting.shareUseHTML = true;
                    }}
                    style={{ boxShadow: '0 0', fontSize: 10 }}
                  >
                    HTML版
                  </Button>
                  <Button
                    variant={userSetting?.shareUseHTML ? 'outlined' : 'contained'}
                    onClick={() => {
                      userSetting.shareUseHTML = false;
                    }}
                    style={{ boxShadow: '0 0', fontSize: 10 }}
                  >
                    テキスト版
                  </Button>
                </ButtonGroup>
              )}
            </ShareBlockHead>
            <ShareBlockBox isMobile={contextValue.isMobile}>
              <CopyTextBox
                ref={candidatesHTMLRef}
                style={{ display: userSetting?.shareUseHTML ? 'block' : 'none' }}
              >
                {userSetting?.shareHTMLWithHead && (
                  <>
                    <CopyText>下記の日程のご都合はいかがでしょうか？</CopyText>
                    <br />
                  </>
                )}
                <table>
                  {Object.values(
                    _.groupBy(
                      _.sortBy(selectedAdjustment?.candidates, c => c.start),
                      c => c.start.format('MM/DD')
                    )
                  ).map(group => (
                    <tr key={group[0].id} style={{ verticalAlign: 'top' }}>
                      <th style={{ display: 'inline-block', padding: '7px 5px 0 0', fontWeight: 500 }}>
                        <CopyText
                          style={{
                            display: 'inline-block',
                            margin: 0,
                            textAlign: 'left',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {group[0].dateText(userSetting?.shareHTMLSlashedDate || false)}
                        </CopyText>
                      </th>
                      <td>
                        {group.map(c =>
                          slotsForCandidate(c).map((s, i) => (
                            <CopyText key={`${c.id}_${i}`} style={{ display: 'inline-block', margin: 0 }}>
                              <span>&nbsp;</span>
                              <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                component="a"
                                style={
                                  /* コピぺのHTML/Text出し分け対応の都合でCSSを直書きしています */ {
                                    display: 'inline-block',
                                    width: '84px',
                                    margin: '3px 0',
                                    padding: '7px 0 8px',
                                    color: muiTheme.palette.primary.main,
                                    border: 'solid 1px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    lineHeight: 1,
                                    userSelect: 'inherit',
                                    textAlign: 'center',
                                    textDecoration: 'none'
                                  }
                                }
                                href={`${location.origin}${history.createHref({
                                  pathname: `/adjustments/${selectedAdjustment?.key}?selectedSlot=${s.start.format(
                                    'YYYY-MM-DD_HH:mm'
                                  )}`
                                })}
                              `}
                              >
                                {`${s.start.format('HH:mm〜')}${s.end.format('HH:mm')}`}
                              </Button>
                            </CopyText>
                          ))
                        )}
                      </td>
                    </tr>
                  ))}
                </table>
                {userSetting?.shareHTMLWithFoot && (
                  <>
                    <br />
                    <CopyText>下記からも日程を確定していただけます。</CopyText>
                    <CopyText>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        style={
                          /* コピぺのHTML/Text出し分け対応の都合でCSSを直書きしています */ {
                            padding: '8px 9px',
                            background: muiTheme.palette.primary.main,
                            color: '#fff',
                            borderRadius: '3px',
                            fontSize: '10px',
                            lineHeight: 1,
                            userSelect: 'inherit',
                            textDecoration: 'none'
                          }
                        }
                        title={`${location.origin}${history.createHref({
                          pathname: `/adjustments/${selectedAdjustment?.key}`
                        })}`}
                        href={`${location.origin}${history.createHref({
                          pathname: `/adjustments/${selectedAdjustment?.key}`
                        })}`}
                      >
                        AIカレンダーで予定を確定
                      </Button>
                      <CopyText>Powered by AIカレンダー</CopyText>
                    </CopyText>
                  </>
                )}
              </CopyTextBox>
              {/* HTML非対応のコピぺ先用 */}
              <CopyPlainTextBox visible={!userSetting?.shareUseHTML} ref={candidatesTextRef}>
                {userSetting?.shareHTMLWithHead && (
                  <>
                    <CopyText>下記の日程のご都合はいかがでしょうか？</CopyText>
                    <br />
                  </>
                )}
                {Object.values(
                  _.groupBy(
                    _.sortBy(selectedAdjustment?.candidates, c => c.start),
                    c => c.start.format('MM/DD')
                  )
                ).map((group, i) => (
                  <CopyText key={group[0].id} style={{ marginTop: i > 0 ? 8 : 0 }}>
                    <span style={{ display: 'inline-block', width: 86 }}>
                      {`${group[0].start.format(
                        userSetting?.shareHTMLSlashedDate ? 'MM/DD(ddd)' : 'M月D日(ddd)'
                      )}`}
                    </span>
                    {group.map((c, j) => (
                      <span key={c.id}>
                        <span>{j > 0 && ','}&nbsp;</span>
                        <span>{`${c.start.format('HH:mm〜')}${c.end.format('HH:mm')}`}</span>
                      </span>
                    ))}
                  </CopyText>
                ))}
                {userSetting?.shareHTMLWithFoot && (
                  <>
                    <br />
                    <CopyText>下記URLから日程を確定していただけます。</CopyText>
                    <CopyText>{`${location.origin}${history.createHref({
                      pathname: `/adjustments/${selectedAdjustment?.key}`
                    })}`}</CopyText>
                    <CopyText>Powered by AIカレンダー</CopyText>
                  </>
                )}
              </CopyPlainTextBox>
              <CopyIconWrap>
                <CopyIconButton
                  edge="end"
                  color="primary"
                  aria-label="copy"
                  onClick={() => copyCandidatesHTMLToClipBoard()}
                >
                  <FilterNoneIcon fontSize="small" style={{ color: '#fff' }} />
                </CopyIconButton>
              </CopyIconWrap>
            </ShareBlockBox>
            {userSetting && (
              <ShareBlockFoot>
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userSetting.shareHTMLWithHead}
                        onChange={e => {
                          userSetting.shareHTMLWithHead = e.target.checked;
                        }}
                        style={{ padding: '3px', color: muiTheme.palette.primary.main }}
                      />
                    }
                    label={<ShareBlockText>文頭のメッセージを表示</ShareBlockText>}
                    style={{ margin: '0' }}
                  />
                </div>
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userSetting.shareHTMLWithFoot}
                        onChange={e => {
                          userSetting.shareHTMLWithFoot = e.target.checked;
                        }}
                        style={{ padding: '3px', color: muiTheme.palette.primary.main }}
                      />
                    }
                    label={<ShareBlockText>調整用URLとメッセージを表示</ShareBlockText>}
                    style={{ margin: '0' }}
                  />
                </div>
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={userSetting.shareHTMLSlashedDate}
                        onChange={e => {
                          userSetting.shareHTMLSlashedDate = e.target.checked;
                        }}
                        style={{ padding: '3px', color: muiTheme.palette.primary.main }}
                      />
                    }
                    label={<ShareBlockText>日付をmm/ddにする</ShareBlockText>}
                    style={{ margin: '0' }}
                  />
                </div>
              </ShareBlockFoot>
            )}
          </ShareBlock>
        </CardBody>
      </Paper>
    </Dialog>
  );

  const handleDrawerOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenDrawer(true);
  };

  const handleDrawerClose = () => {
    if (!openDrawer) {
      return;
    }

    setOpenDrawer(false);
  };

  const handleDrawersClose = () => {
    if (!openDrawer && !openOpenMeetingDrawer) {
      return;
    }

    setSelectedAdjustment(null);
    if (!contextValue.isMobile) {
      window.setTimeout(() => {
        setOpenDrawer(false);
        setOpenOpenMeetingDrawer(false);
        setOpenLeftColumn(false);
      }, 100);
    }
  };

  const slackCallbackFlag: boolean = location.hash === '#slack_complete';

  const handleChangeMeetingType = (meetingType: AdjustmentMeetingType) => {
    if (editingAdjustment && (meetingType !== 'zoom' || contextValue.user?.appointZoomAccessToken)) {
      editingAdjustment.meetingType = meetingType;
    } else {
      setShowZoomAuthDialog(true);
    }
  };

  const renderZoomAuthDialog = () => (
    <ZoomAuthDialog open={showZoomAuthDialog} onClose={() => setShowZoomAuthDialog(false)}>
      <Paper style={{ position: 'relative' }}>
        <IconButton
          size="small"
          onClick={() => setShowZoomAuthDialog(false)}
          style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <ZoomAuthContent>
          <img
            src="/images/aicalendar/zoom_auth.png"
            height="40"
            alt="Zoomと連携する"
            style={{ display: 'block' }}
          />
          <p style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.2em' }}>
            Zoomにログインして
            <br />
            連携をしますか？
          </p>
        </ZoomAuthContent>
        <ZoomAuthButtonWrap>
          <Button variant="contained" onClick={() => setShowZoomAuthDialog(false)}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              location.href = '/aicalendar/zoom/authorization/new';
            }}
          >
            連携
          </Button>
        </ZoomAuthButtonWrap>
      </Paper>
    </ZoomAuthDialog>
  );

  const getOnlineMeetingName = (meetingType: string) => {
    switch (meetingType) {
      case 'google':
        return 'Google Meet';
      case 'outlook':
        return 'Microsoft Teams';
      case 'zoom':
        return 'Zoom';
      default:
        return 'なし';
    }
  };

  if (contextValue.user && !showNameSettingFirstFlag) {
    if (!contextValue.user?.needAppointDashboardCoachMark && contextValue.user?.name === ' ') {
      setShowNameSettingFirstFlag(true);
      setShowNameSettingDialog(true);
    }
  }

  const updateUserName = async () => {
    const user = contextValue.user;
    if (user) {
      const params = { user: { name: userName } };
      await axios.put('/aicalendar/user', params);
      location.reload();
    }
  };

  const renderNameSettingDialog = () => (
    <NameSettingDialog open={showNameSettingDialog} onClose={() => setShowNameSettingDialog(false)}>
      <Paper style={{ position: 'relative' }}>
        <IconButton
          size="small"
          onClick={() => setShowNameSettingDialog(false)}
          style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <NameSettingContent>
          <NameSettingDialogHeadIcon>
            <EventNoteIcon fontSize="large" />
          </NameSettingDialogHeadIcon>
          <p
            style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '14px', margin: '0px', color: '#243565' }}
          >
            お名前を記入して連携を完了
          </p>
          <p
            style={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '10px',
              marginTop: '0px',
              color: '#243565'
            }}
          >
            表示される名前を入力して設定を完了させてください。
          </p>
          <TheTextField
            label="お名前"
            value={userName}
            placeholder="例：山田 太郎"
            InputLabelProps={{ shrink: true }}
            style={{ width: '100%' }}
            onChange={e => setUserName(e.target.value)}
          />
        </NameSettingContent>
        <NameSettingButtonWrap>
          <Button
            variant="contained"
            color="primary"
            disabled={userName.trim().length === 0}
            onClick={() => {
              setShowNameSettingDialog(false);
              updateUserName();
            }}
          >
            決定
          </Button>
        </NameSettingButtonWrap>
      </Paper>
    </NameSettingDialog>
  );

  const getReturnInString = (text: string) => {
    const regex = /(\n)/g;
    return text.split(regex).map(line => {
      if (line.match(regex)) {
        return <br />;
      }
      return line;
    });
  };

  const getExcludeAdjustmentTitle = (adjustmentTitle: string) => {
    let startIdx = 0;
    if (adjustmentTitle.substring(startIdx, 5) === '【調整中】') {
      startIdx = 5;
    }
    return adjustmentTitle.substring(startIdx);
  };

  return (
    <>
      <TheContainer maxWidth={false} onClick={() => handleDrawersClose()}>
        {contextValue.isMobile && (
          <FixedNewAdjustmentButton>
            <IconButton component={Link} to="/adjustments/new">
              <AddCircleIcon fontSize="large" style={{ color: muiTheme.palette.primary.main, fontSize: 52 }} />
            </IconButton>
          </FixedNewAdjustmentButton>
        )}
        <AuthBackdrop open={needAuth}>
          <Box>
            <Typography style={{ textAlign: 'center' }}>ログインしてGoogle Calendarと連携</Typography>
            <Button
              style={{ display: 'block', margin: '0 auto' }}
              onClick={() => {
                location.href = history.createHref({ pathname: '/auth/new?provider=google' });
              }}
            >
              <img src="/images/sign_in_with_google.png" width="191" />
            </Button>
          </Box>
          <Box>
            <Typography style={{ textAlign: 'center' }}>ログインしてOutlook Calendarと連携</Typography>
            <Button
              style={{ display: 'block', margin: '0 auto' }}
              onClick={() => {
                location.href = history.createHref({ pathname: '/auth/new?provider=outlook' });
              }}
            >
              <img src="/images/sign_in_with_microsoft.png" width="215" />
            </Button>
          </Box>
        </AuthBackdrop>
        <AuthBackdrop open={needReAuth}>
          <Box>
            <Typography style={{ textAlign: 'center' }}>
              {disconnectedAccounts.map((da, i) => (
                <p key={i}>
                  {da.message
                    ? `Google Calendar(${da.email})への${da.message}`
                    : `Google Calendar(${da?.email})との連携が解除されています`}
                </p>
              ))}
            </Typography>
            <TheReAuthButton
              variant="contained"
              color="primary"
              style={{ display: 'block', margin: '12px auto 0', textTransform: 'none' }}
              onClick={() => {
                location.href = history.createHref({ pathname: '/auth/new?provider=google' });
              }}
            >
              Google Calendarと再連携
            </TheReAuthButton>
          </Box>
        </AuthBackdrop>
        <LeftMenu
          isLoading={isLoading}
          openLeftColumn={openLeftColumn}
          openDrawer={openDrawer}
          openOpenMeetingDrawer={openOpenMeetingDrawer}
          adjustments={adjustments}
          selectedCalendars={selectedCalendars}
          selectedAdjustment={selectedAdjustment}
          currentStatusTab={currentStatusTab}
          onCurrentStatusTabChange={status => handleCurrentStatusTabChange(status)}
          adjustmentsFinished={adjustmentsFinished}
          isAdjustmentLoading={isAdjustmentLoading}
          containerRef={leftContainerRef}
          newAdjustmentButtonRef={newAdjustmentButtonRef}
          adjustmentsLoader={adjustmentsLoader}
          onDrawerOpen={e => handleDrawerOpen(e)}
          onOpenMeetingDrawerOpen={() => setOpenOpenMeetingDrawer(true)}
          onSelectedCalendarChange={(calendar, checked) => handleSelectedCalendarsChange(calendar, checked)}
          onAdjustmentDrawerClose={() => handleDrawerClose()}
          onOpenMeetingDrawerClose={() => setOpenOpenMeetingDrawer(false)}
          onDetailOpen={key => handleDetailOpen(key)}
        />
        <TheRight
          ref={rightContainerRef}
          isMobile={contextValue.isMobile}
          detailOpened={`${openDrawer && selectedAdjustment !== null}`}
        >
          <Drawer
            open={openDrawer && selectedAdjustment !== null}
            anchor="left"
            variant="persistent"
            onClick={e => e.stopPropagation()}
            onClose={() => setSelectedAdjustment(null)}
            PaperProps={{
              style: {
                position: contextValue.isMobile ? 'fixed' : 'unset',
                zIndex: 101,
                boxSizing: 'border-box',
                minWidth: contextValue.isMobile ? '100vw' : '100%',
                maxWidth: '280px',
                borderTop: 'solid 10px #243565',
                borderRight: '0'
              }
            }}
            ModalProps={{
              container: rightContainerRef.current
            }}
            style={{
              marginRight: !contextValue.isMobile && (!openDrawer || selectedAdjustment) ? '24px' : '0'
            }}
          >
            {openDrawer && (
              <DrawerIn
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  boxSizing: 'border-box',
                  height: '100%',
                  padding: openDrawer && selectedAdjustment ? '20px' : '0',
                  borderRight: openDrawer && selectedAdjustment ? 'solid 1px #ededed' : '0'
                }}
              >
                <DetailDrawerHead>
                  <CloseButton onClick={() => handleDrawersClose()} />
                  {selectedAdjustment && !selectedAdjustment.cancelled && (
                    <EditIconButtonsBox>
                      <EditIconButton
                        size="small"
                        onClick={() => toggleIsAdjustmentEditing()}
                        startIcon={<CreateIcon />}
                        style={{ color: isAdjustmentEditing ? muiTheme.palette.primary.main : '#909090' }}
                      >
                        {`編集${isAdjustmentEditing ? '中' : ''}`}
                      </EditIconButton>
                      {selectedAdjustment.adjusting && (
                        <EditIconButton
                          size="small"
                          onClick={() => setShowCancelDialog(true)}
                          startIcon={<DeleteSweepIcon />}
                          style={{ color: '#909090', marginRight: '-7px' }}
                        >
                          削除
                        </EditIconButton>
                      )}
                    </EditIconButtonsBox>
                  )}
                </DetailDrawerHead>
                {selectedAdjustment && (
                  <DrawerBody style={{ width: contextValue.isMobile ? '100%' : '240px' }}>
                    <DetailDrawerListItem style={{ display: 'block', padding: '0' }}>
                      {isAdjustmentEditing && editingAdjustment ? (
                        <AutocompleteBox withAlert={`${isTitleEmpty()}`}>
                          <Autocomplete
                            options={meetingTitleHistories}
                            getOptionLabel={history => history.title || ''}
                            inputValue={getExcludeAdjustmentTitle(editingAdjustment.title)}
                            filterOptions={(options, state) => {
                              const q = state.inputValue;
                              return _.filter(options, o => o.title?.indexOf(q) > -1);
                            }}
                            disabled={!isAdjustmentEditing}
                            onInputChange={(e, value) => {
                              // enter押下時(eventType === 'onKeyDown')にタイトルを空にしない。
                              const eventType = Utils.dig(
                                e,
                                'dispatchConfig',
                                'phasedRegistrationNames',
                                'bubbled'
                              );
                              if (typeof value === 'string' && eventType !== null && eventType !== 'onKeyDown') {
                                editingAdjustment.title = value;
                              }
                            }}
                            onChange={(_, selected) => {
                              if (selected && typeof selected !== 'string') {
                                editingAdjustment.title = selected.title;
                              }
                            }}
                            openOnFocus
                            freeSolo
                            renderInput={params => (
                              <TheTextField
                                {...params}
                                label="タイトル"
                                placeholder="タイトルを入力"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            style={{ width: '100%' }}
                          />
                        </AutocompleteBox>
                      ) : (
                        <TheListItemText
                          disableTypography
                          primary={<AdjDetailTitle>タイトル</AdjDetailTitle>}
                          secondary={
                            <AdjDetailLargeText>
                              {getExcludeAdjustmentTitle(selectedAdjustment.title)}
                            </AdjDetailLargeText>
                          }
                        />
                      )}
                    </DetailDrawerListItem>
                    <DetailDrawerListItem style={{ display: 'block', padding: '0' }}>
                      <TheListItemText
                        disableTypography
                        primary={<AdjDetailTitle>確定日時</AdjDetailTitle>}
                        secondary={
                          <>
                            {selectedAdjustment.fixed ? (
                              <>
                                {isAdjustmentEditing && editingAdjustment ? (
                                  <>
                                    <DetailDrawerDateTimeBox>
                                      <DetailDrawerDateTimeLabel>開始</DetailDrawerDateTimeLabel>
                                      <MuiPickersUtilsProvider utils={MomentUtils}>
                                        <Box style={{ position: 'relative' }}>
                                          <DatePicker
                                            autoOk
                                            disableToolbar
                                            disablePast
                                            variant="inline"
                                            format="MM/DD(dd)"
                                            margin="none"
                                            value={editingAdjustment.startTime}
                                            onChange={dateTime => {
                                              editingAdjustment.startTime = moment(dateTime?.toDate());
                                            }}
                                          />
                                          <DatePickerIcon>
                                            <ArrowDropDownIcon />
                                          </DatePickerIcon>
                                        </Box>
                                      </MuiPickersUtilsProvider>
                                      <FormControl style={{ minWidth: '90px' }}>
                                        <DetailDrawerTimeSelect
                                          id="demo-controlled-open-select"
                                          value={editingAdjustment.startTime?.format('HH:mm')}
                                          onChange={e => handleAdjustmentTimeUpdate('start', e.target.value)}
                                        >
                                          {Array.from(Array(24).keys()).map(h =>
                                            ['00', '15', '30', '45'].map(m => (
                                              <MenuItem key={`${h}_${m}`} value={`${h < 10 ? `0${h}` : h}:${m}`}>
                                                {`${h < 10 ? `0${h}` : h}:${m}`}
                                              </MenuItem>
                                            ))
                                          )}
                                        </DetailDrawerTimeSelect>
                                      </FormControl>
                                    </DetailDrawerDateTimeBox>
                                    <DetailDrawerDateTimeBox>
                                      <DetailDrawerDateTimeLabel>終了</DetailDrawerDateTimeLabel>
                                      <MuiPickersUtilsProvider utils={MomentUtils}>
                                        <Box style={{ position: 'relative' }}>
                                          <DatePicker
                                            autoOk
                                            disableToolbar
                                            disablePast
                                            variant="inline"
                                            format="MM/DD(dd)"
                                            margin="none"
                                            value={editingAdjustment.endTime}
                                            onChange={dateTime => {
                                              editingAdjustment.endTime = moment(dateTime?.toDate());
                                            }}
                                          />
                                          <DatePickerIcon>
                                            <ArrowDropDownIcon />
                                          </DatePickerIcon>
                                        </Box>
                                      </MuiPickersUtilsProvider>
                                      <FormControl style={{ minWidth: '90px' }}>
                                        <DetailDrawerTimeSelect
                                          id="demo-controlled-open-select"
                                          value={editingAdjustment.endTime?.format('HH:mm')}
                                          onChange={e => handleAdjustmentTimeUpdate('end', e.target.value)}
                                        >
                                          {Array.from(Array(24).keys()).map(h =>
                                            ['00', '15', '30', '45'].map(m => (
                                              <MenuItem key={`${h}_${m}`} value={`${h < 10 ? `0${h}` : h}:${m}`}>
                                                {`${h < 10 ? `0${h}` : h}:${m}`}
                                              </MenuItem>
                                            ))
                                          )}
                                        </DetailDrawerTimeSelect>
                                      </FormControl>
                                    </DetailDrawerDateTimeBox>
                                  </>
                                ) : (
                                  <AdjDetailText>{selectedAdjustment.dateText()}</AdjDetailText>
                                )}
                              </>
                            ) : (
                              <>
                                <CandidateField
                                  status={
                                    selectedAdjustment.cancelled
                                      ? 'cancelled'
                                      : selectedAdjustment.isPast
                                      ? 'past'
                                      : 'active'
                                  }
                                  onClick={() => handleOpenCandidateDialog()}
                                >
                                  <span style={{ marginRight: 'auto' }}>日程が確定されていません</span>
                                  <span className="icon">
                                    <ArrowDropDownIcon />
                                  </span>
                                </CandidateField>
                                {renderCandidateDialog()}
                              </>
                            )}
                          </>
                        }
                      />
                    </DetailDrawerListItem>
                    <DetailDrawerListItem style={{ display: 'block', padding: '0' }}>
                      <TheListItemText
                        disableTypography
                        primary={<AdjDetailTitle>時間</AdjDetailTitle>}
                        secondary={<AdjDetailText>{`${selectedAdjustment.duration}分`}</AdjDetailText>}
                      />
                    </DetailDrawerListItem>
                    <DetailDrawerListItem style={{ display: 'block', padding: '0' }}>
                      {isAdjustmentEditing && editingAdjustment ? (
                        <Box display="flex">
                          <Box flexGrow={999}>
                            <PlaceInput
                              value={editingAdjustment.place}
                              label="場所"
                              placeholder="場所を追加"
                              disabled={!isAdjustmentEditing}
                              onInputChange={value => {
                                editingAdjustment.place = value;
                                if (value.length === 0) {
                                  editingAdjustment.placeAddress = '';
                                }
                              }}
                              onChange={value => {
                                if (!value || typeof value === 'string') {
                                  return;
                                }
                                editingAdjustment.placeAddress = value.subText;
                              }}
                              helperText={editingAdjustment.placeAddress}
                            />
                          </Box>
                        </Box>
                      ) : (
                        <TheListItemText
                          disableTypography
                          primary={<AdjDetailTitle>場所</AdjDetailTitle>}
                          secondary={<AdjDetailText>{selectedAdjustment.place || '指定なし'}</AdjDetailText>}
                        />
                      )}
                    </DetailDrawerListItem>
                    <DetailDrawerListItem style={{ display: 'block', padding: '0' }}>
                      {isAdjustmentEditing && editingAdjustment ? (
                        <TextField
                          label="メモ"
                          placeholder="メモを追加"
                          value={editingAdjustment.description || ''}
                          multiline
                          onChange={e => {
                            editingAdjustment.description = e.target.value;
                          }}
                          InputLabelProps={{ shrink: true }}
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <TheListItemText
                          disableTypography
                          primary={<AdjDetailTitle>メモ</AdjDetailTitle>}
                          secondary={
                            <AdjDetailText>
                              {getReturnInString(selectedAdjustment.description || '')}
                            </AdjDetailText>
                          }
                        />
                      )}
                    </DetailDrawerListItem>
                    <DetailDrawerListItem style={{ display: 'block', padding: '0' }}>
                      {isAdjustmentEditing && editingAdjustment ? (
                        <ParticipantInput
                          isLoading={isLoading}
                          participants={editingAdjustment.participants}
                          calendars={calendars}
                          onAdd={handleAddParticipant}
                          onRemove={handleRemoveParticipant}
                        />
                      ) : (
                        <TheListItemText
                          disableTypography
                          primary={<AdjDetailTitle>参加者</AdjDetailTitle>}
                          secondary={
                            <AdjDetailText>
                              {selectedAdjustment.participants.map(p => (
                                <span key={p.id}>{p.name}</span>
                              ))}
                            </AdjDetailText>
                          }
                        />
                      )}
                    </DetailDrawerListItem>
                    <DetailDrawerListItem style={{ display: 'block', padding: '0' }}>
                      {isAdjustmentEditing && editingAdjustment ? (
                        <FormControl style={{ width: '100%' }}>
                          <InputLabel shrink htmlFor="adjustment-meeting-type">
                            Web会議
                          </InputLabel>
                          <NativeSelect
                            value={editingAdjustment.meetingType}
                            inputProps={{
                              name: 'meeting_type',
                              id: 'adjustment-meeting-type'
                            }}
                            onChange={e => handleChangeMeetingType(e.target.value as AdjustmentMeetingType)}
                          >
                            <option value="none">なし</option>
                            {calendars.some(c => c.provider === 'google') && (
                              <option value="google">Google Meet</option>
                            )}
                            <option value="zoom">Zoom</option>
                          </NativeSelect>
                        </FormControl>
                      ) : (
                        <TheListItemText
                          disableTypography
                          primary={<AdjDetailTitle>Web会議</AdjDetailTitle>}
                          secondary={
                            <AdjDetailLargeText>
                              {getOnlineMeetingName(selectedAdjustment.meetingType || '')}
                            </AdjDetailLargeText>
                          }
                        />
                      )}
                      <Box>{renderZoomAuthDialog()}</Box>
                    </DetailDrawerListItem>
                    {isAdjustmentEditing ? (
                      <div style={{ textAlign: 'right' }}>
                        <DrawerBottomButton
                          variant="contained"
                          size="large"
                          color="primary"
                          onClick={() => handleAdjustmentUpdate()}
                          disabled={isAdjustmentUpdating}
                          style={{ width: 'auto' }}
                        >
                          変更を保存する
                        </DrawerBottomButton>
                        {adjustmentUpdaterrorMessage && <Error>{adjustmentUpdaterrorMessage}</Error>}
                      </div>
                    ) : selectedAdjustment?.adjusting ? (
                      <DrawerBottomButton
                        variant="outlined"
                        color="primary"
                        onClick={() => setShowShareDialog(true)}
                        endIcon={<ShareIcon />}
                      >
                        日程調整をシェアする
                      </DrawerBottomButton>
                    ) : selectedAdjustment?.fixed ? (
                      <>
                        {selectedAdjustment.onlineMeetingUrl() && (
                          <DetailDrawerListItem style={{ display: 'block', padding: '0' }}>
                            <TheListItemText
                              disableTypography
                              primary={<AdjDetailTitle>Web会議リンク</AdjDetailTitle>}
                              secondary={
                                <OnlineMeetingUrl>
                                  <OnlineMeetingUrlText ref={onlineMeetingUrlRef}>
                                    {selectedAdjustment.onlineMeetingUrl()}
                                  </OnlineMeetingUrlText>
                                  <CopyIconWrap>
                                    <CopyIconButton
                                      edge="end"
                                      color="primary"
                                      aria-label="copy"
                                      onClick={() => copyOnlineMeetingUrlToClipBoard()}
                                    >
                                      <FilterNoneIcon fontSize="small" style={{ color: '#fff' }} />
                                    </CopyIconButton>
                                  </CopyIconWrap>
                                </OnlineMeetingUrl>
                              }
                            />
                          </DetailDrawerListItem>
                        )}
                        <DrawerBottomButton
                          variant="outlined"
                          onClick={() => setShowCancelDialog(true)}
                          style={{ color: '#909090' }}
                        >
                          キャンセルして予定を削除
                        </DrawerBottomButton>
                      </>
                    ) : null}
                  </DrawerBody>
                )}
              </DrawerIn>
            )}
            {renderShareDialog()}
            {renderCancelDialog()}
            {renderNameSettingDialog()}
          </Drawer>
          <BigCalendarWrap isMobile={contextValue.isMobile}>
            <Dialog open={slackCallbackFlag} fullWidth>
              <SlackCallBackBox>
                <SlackCallBackIconButton size="small">
                  <CloseButton
                    fontSize="small"
                    onClick={() => {
                      location.href = history.createHref({ pathname: '/' });
                    }}
                  />
                </SlackCallBackIconButton>
                <SlackCallBackExampleImageBox src="images/aicalendar/slack_example.png" />
                <SlackCallBackUnderBox>
                  <SlackCallBackIconBox src="images/aicalendar/slack_icon1.png" />
                  <SlackCallBackText1>Slackとの連携が可能になりました。</SlackCallBackText1>
                  <SlackCallBackText2>
                    slackの連携したいチャンネルを選択、 AIカレンダーのアプリを追加するとご利用が可能になります。
                  </SlackCallBackText2>
                  <SlackCallBackHelpButton
                    onClick={() => {
                      window.open(
                        'https://dune-horse-f28.notion.site/slack-7ae132875f3243cb8960af1339563191',
                        '_blank'
                      );
                    }}
                  >
                    設定方法を見る
                  </SlackCallBackHelpButton>
                </SlackCallBackUnderBox>
              </SlackCallBackBox>
            </Dialog>
            <BigCalendar
              eventLists={Object.values(eventLists)}
              calendars={calendars}
              selectedCalendars={selectedCalendars}
              date={date}
              holidays={holidays}
              isEventLoading={isEventLoading}
              onDateChange={date => handleDateChange(date)}
              handleDetailOpen={handleDetailOpen}
              onCreateEvent={() => fetchEvents(selectedCalendars)}
              onUpdateEvent={() => fetchEvents(selectedCalendars)}
              onDeleteEvent={() => fetchEvents(selectedCalendars)}
            />
          </BigCalendarWrap>
        </TheRight>
      </TheContainer>
      {contextValue.user?.needAppointDashboardCoachMark && (
        <DashboardCoachMark newAdjustmentButtonRef={newAdjustmentButtonRef} menuButtonRef={menuButtonRef} />
      )}
      {contextValue.isMobile && (
        <Drawer
          open={openHeadDrawer}
          anchor={contextValue.isMobile ? 'left' : 'right'}
          onClose={() => setOpenHeadDrawer(false)}
        >
          <DrawerMenu
            onNavigate={() => setOpenHeadDrawer(false)}
            setOpen={setOpenHeadDrawer}
            onAdjustmentlistOpen={handleDrawerOpen}
            onOpenMeetingDrawerOpen={() => setOpenOpenMeetingDrawer(true)}
            selectedCalendars={selectedCalendars}
            handleSelectedCalendarsChange={handleSelectedCalendarsChange}
          />
        </Drawer>
      )}
    </>
  );
});

const DialogHead = styled(Box)`
  padding: 20px 64px;
  color: ${muiTheme.palette.primary.main};
  text-align: center;
`;

const DialogHeadIcon = styled.div`
  margin-bottom: 2px;
`;

const DialogHeadTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
`;

const DialogHeadText = styled(Typography)`
  font-size: 11px;
  font-weight: 700;
`;

const DialogContent = styled(Box)<{ isMobile: boolean }>`
  padding: 0 64px 30px;

  ${props =>
    props.isMobile &&
    `
    padding: 0 16px 16px;
  `}
`;

const DialogAdjustmentWrap = styled(Box)`
  padding: 20px;
  border: solid 1px #ededed;
  border-radius: 3px;
`;

const DialogAdjustmentTitle = styled(Typography)`
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: bold;
  line-height: 1.6;
`;

const DialogAdjustmentDate = styled(Typography)`
  color: #606060;
  font-size: 10px;
  line-height: 1.6;
  letter-spacing: 0.05em;
`;

const DialogFoot = styled(Box)<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  padding: 20px 64px;
  background: #f0f0f0;

  ${props =>
    props.isMobile &&
    `
    padding: 16px;
  `}
`;

const DialogFootText = styled(Typography)`
  margin-right: auto;
  color: #606060;
  font-size: 11px;
  font-weight: bold;
`;

const Error = styled(Typography).attrs(() => ({ color: 'error' }))`
  font-size: 10px;
`;

const CardHead = styled(Box)<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  padding: 30px 30px 24px;

  ${props =>
    props.isMobile &&
    `
    flex-direction: column-reverse;
    align-items: start;
    padding: 16px;
  `}
`;

const CardHeadTitle = styled(Typography)`
  font-size: 16px;
`;

const CardHeadText = styled(Typography)<{ isMobile: boolean }>`
  font-size: 10px;

  ${props =>
    props.isMobile &&
    `
    width: 100%;
    text-align: center;
    margin-bottom: 10px;
  `}
`;

const CardBody = styled(Box)<{ isMobile: boolean }>`
  padding: 0 20px;

  ${props =>
    props.isMobile &&
    `
    padding: 0;
  `}
`;

const ShareBlock = styled(Box)<{ isMobile: boolean }>`
  margin-bottom: 24px;
  padding: 0 20px;

  ${props =>
    props.isMobile &&
    `
    padding: 0;
  `}
`;

const ShareBlockHead = styled(Box)`
  display: flex;
  align-items: flex-end;
  font-weight: bold;
`;

const ShareBlockTitle = styled(Typography)`
  font-size: 12px;
`;

const PreviewButton = styled(Button)`
  margin-left: auto;
  font-size: 10px;
  line-height: 1;

  .MuiButton-endIcon {
    margin-left: 3px;

    svg {
      font-size: 15px;
    }
  }
`;

const ShareBlockBox = styled(Box)<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 450px;
  margin: 6px 0;
  padding: 10px 12px;
  border: solid 1px #ddd;
  border-radius: 3px;

  ${props =>
    props.isMobile &&
    `
    width: auto;
  `}
`;

const CopyIconWrap = styled(Box)`
  min-width: 36px;
  margin-bottom: auto;
  background: #fff;
  box-shadow: -21px 0px 38px 40px #fff;
`;

const CopyIconButton = styled(IconButton)`
  padding: 8px;
  background: ${muiTheme.palette.primary.main};
  box-shadow: 0 0 6px -2px #000;
  border-radius: 50%;
  line-height: 1;
  transform: rotateX(180deg);
`;

const CopyTextBox = styled.div`
  flex-grow: 999;
  max-width: calc(100% - 36px);
`;

const CopyText = styled(Typography)`
  font-size: 13px;
  line-break: anywhere;
`;

const CopyPlainTextBox = styled.div<{ visible: boolean }>`
  width: 0;
  height: 0;
  opacity: 0;

  ${props =>
    props.visible &&
    `
    width: calc(100% - 36px);
    height: auto;
    opacity: 1;
  `}
`;

const ShareBlockFoot = styled(Box)``;

const ShareBlockText = styled(Typography)`
  color: #606060;
  font-size: 12px;
`;

const CardFoot = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 20px;
  background: #f9f9f9;
`;

const SubmitButton = styled(Button)`
  background: ${muiTheme.palette.primary.main};
  color: #fff;
  font-size: 12px;

  &.Mui-disabled {
    background: ${muiTheme.palette.action.disabledBackground};
  }
`;

const ShareHead = styled(Box)`
  margin-bottom: 25px;
  color: ${muiTheme.palette.primary.main};
  text-align: center;
`;

const ShareHeadIcon = styled.div`
  margin-bottom: 5px;
`;

const ShareTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
`;

const ShareText = styled(Typography)`
  font-size: 11px;
  font-weight: 700;
`;

const TheContainer = styled(Container)`
  display: flex;
  min-height: calc(100vh - 54px);
  padding: 0;
`;

const FixedNewAdjustmentButton = styled(Button)`
  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 9;
`;

export const TheBox = styled(Box)`
  flex-grow: 1;
  overflow: auto;
  box-sizing: border-box;
  margin-top: 20px;
  height: calc(100vh - 54px - 24px - 39px - 36px - 20px - 12px - 24px);
  padding: 0 20px;
`;

const DrawerIn = styled(Box)`
  box-sizing: border-box;
`;

const DetailDrawerHead = styled(Box)`
  margin-bottom: 16px;
  text-align: right;
`;

const DetailDrawerListItem = styled(Box)`
  margin-bottom: 12px;

  label.MuiInputLabel-root {
    color: #000;
    font-size: 10px;
    font-weight: bold;
    transform: scale(1);
  }
`;

const DetailDrawerDateTimeBox = styled(Box)`
  display: flex;
  align-items: center;

  .MuiFormControl-root {
    flex-grow: 999;
  }
`;

const DatePickerIcon = styled(Box)`
  position: absolute;
  top: calc(50% - 12px);
  right: 0;
  color: rgba(0, 0, 0, 0.54);
  pointer-events: none;
`;

const DetailDrawerDateTimeLabel = styled(Typography)`
  min-width: 20px;
  margin-right: 6px;
  color: #909090;
  font-size: 8px;
`;

const DetailDrawerTimeSelect = styled(Select)`
  .MuiSelect-select.MuiSelect-select {
    padding: 8px 24px 5px 6px;
  }
`;

const EditIconButtonsBox = styled(Box)`
  display: flex;
  justify-content: flex-end;
`;

const EditIconButton = styled(Button)`
  margin-top: 12px;
  font-size: 10px;
`;

const DrawerBody = styled(Box)``;

const DrawerBottomButton = styled(Button)`
  width: 100%;
  margin: 24px 0 12px;
  font-size: 12px;

  .MuiButton-endIcon {
    margin-left: auto;
  }
`;

const TheListItemText = styled(ListItemText)`
  overflow: hidden;
  margin: 0;
`;

const TheRight = styled(Right)<{ isMobile: boolean; detailOpened: string }>`
  display: flex;
  border-left: solid 1px ${props => props.theme.grayBorderColor};

  ${props =>
    props.detailOpened === 'true' &&
    `
    margin-right: -280px;

    .rbc-toolbar {
      width: calc(100% - 44px + 280px);
      margin-left: -280px;
    }
  `}

  ${props =>
    props.isMobile &&
    `
    max-height: calc(100vh - 54px);
    margin-right: 0;
    padding: 0 6px;
    border-left: 0;
  `}

  .rbc-toolbar button {
    padding: 0.275rem 1rem;
  }
`;

const AdjDetailTitle = styled(Typography)`
  margin-bottom: 5px;
  font-size: 10px;
  font-weight: bold;
`;

const AdjDetailText = styled(Typography)`
  color: ${muiTheme.palette.primary.main};
  font-size: 12px;
  font-weight: bold;

  span {
    display: block;
  }
`;

const AdjDetailLargeText = styled(AdjDetailText)`
  font-size: 14px;
`;

const TheTextField = styled(TextField)`
  font-size: 12px;
`;

const OnlineMeetingUrl = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`;

const OnlineMeetingUrlText = styled(Typography)`
  overflow: hidden;
  white-space: nowrap;
  font-size: 12px;
`;

const CandidateField = styled.div<{ status: string }>`
  display: flex;
  border-bottom: solid 2px;
  color: ${props => (props.status === 'active' ? '#bf0000' : muiTheme.palette.primary.main)};
  cursor: pointer;
`;

const BigCalendarWrap = styled.div<{ isMobile: boolean }>`
  flex-grow: 1;
  padding: 12px 20px 0 0;

  ${props =>
    props.isMobile &&
    `
    padding: 0;
  `}
`;

const SlackCallBackBox = styled.div`
  left: 340px;
  top: 211px;
  width: 600px;
  height: 507px;
  background: #1b1d21;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
`;

const SlackCallBackIconButton = styled(IconButton)`
  position: absolute;
  width: 30px;
  height: 30px;
  left: 562px;
  top: 6px;
`;

const SlackCallBackExampleImageBox = styled.img`
  position: absolute;
  width: 495px;
  height: 391px;
  left: 72px;
  top: 13px;
`;

const SlackCallBackIconBox = styled.img`
  position: absolute;
  width: 40px;
  height: 40px;
  left: 280px;
  top: 23px;
  z-index: 100;
`;

const SlackCallBackUnderBox = styled.div`
  position: absolute;
  width: 600px;
  height: 260px;
  left: 0px;
  top: 247px;
  background: #ffffff;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.2);
`;

const SlackCallBackText1 = styled(Typography)`
  position: absolute;
  width: 300px;
  height: 0px;
  left: 160px;
  top: 93px;
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 0%;
  text-align: center;
  letter-spacing: 0.05em;
  color: #243565;
`;

const SlackCallBackText2 = styled(Typography)`
  position: absolute;
  width: 322px;
  height: 40px;
  left: 139px;
  top: 114px;
  font-family: Noto Sans JP;
  font-style: normal;
  font-weight: 500;
  font-size: 11px;
  line-height: 180%;
  text-align: center;
  letter-spacing: 0.05em;
  color: #000000;
  mix-blend-mode: normal;
`;

const SlackCallBackHelpButton = styled(Button)`
  position: absolute;
  width: 140px;
  height: 32px;
  top: 187px;
  left: 230px;
  background: #243565;
  border: 1px solid #dddddd;
  box-sizing: border-box;
  border-radius: 3px;
  color: #ffffff;
  &:hover {
    background: #243565;
    border-radius: 3px;
  }
`;

const NameSettingDialog = styled(Dialog)``;

const NameSettingDialogHeadIcon = styled.div`
  text-align: center;
  margin-top: -30px;
`;

const NameSettingContent = styled(Box)`
  padding: 64px 56px 14px;
`;

const NameSettingButtonWrap = styled(Box)`
  display: flex;
  justify-content: center;
  background-color: #f9f9f9;
  padding: 14px 0;

  & button:first-child {
    margin-right: 10px;
  }

  & button {
    width: 105px;
  }
`;

const ZoomAuthDialog = styled(Dialog)``;

const ZoomAuthContent = styled(Box)`
  padding: 64px 56px 14px;
`;

const ZoomAuthButtonWrap = styled(Box)`
  display: flex;
  justify-content: center;
  background-color: #f9f9f9;
  padding: 14px 0;

  & button:first-child {
    margin-right: 10px;
  }

  & button {
    width: 105px;
  }
`;

export default Dashboard;
